import { useCallback } from 'react';
import { Icon, useModal } from '@pelando/components';
import { Flag } from '@pelando/fontawesome/icons';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import { LockScrollGlobally } from '@/presentation/styles/global';

import { ModalContent } from '@/infra/blog/strapi/types';
import {
  CashbackIcon,
  Container,
  Subtitle,
  Title,
  Tip,
  EnableCashbackButton,
  GoToStoreWithoutCashbackButton,
  modalStyle,
} from './style';

type EnableCashbackModalProps = {
  tip: string;
  subtitle: string;
  onEnableCashback: () => void;
  onGoToStoreWithoutCashback: () => void;
};

export const EnableCashbackModal = ({
  tip,
  subtitle,
  onEnableCashback,
  onGoToStoreWithoutCashback,
}: EnableCashbackModalProps) => {
  const { t } = useTranslation('common');

  const DEFAULT_TIP = t('enable-cashback-modal-default-tip');

  const tipText = tip || DEFAULT_TIP;

  return (
    <Container>
      <CashbackIcon />
      <Title>{t('enable-cashback-modal-title')}</Title>
      <Subtitle>{subtitle}</Subtitle>
      <Tip>
        <Icon icon={Flag} />
        <span>{tipText}</span>
      </Tip>
      <EnableCashbackButton onClick={onEnableCashback}>
        {t('enable-cashback-modal-enable-button')}
      </EnableCashbackButton>
      <GoToStoreWithoutCashbackButton onClick={onGoToStoreWithoutCashback}>
        {t('enable-cashback-modal-go-to-store-without-cashback')}
      </GoToStoreWithoutCashbackButton>
    </Container>
  );
};

type UseEnableCashbackModalProps = {
  modalContent: ModalContent;
};

export const useEnableCashbackModal = ({
  modalContent,
}: UseEnableCashbackModalProps) => {
  const { showModal, closeModal } = useModal();

  const createHandlerWithCloseModal = useCallback(
    (handler: () => void) => () => {
      handler();
      closeModal();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const showEnableCashbackModal = useCallback(
    ({
      onEnableCashback,
      onGoToStoreWithoutCashback,
    }: {
      onEnableCashback: () => void;
      onGoToStoreWithoutCashback: () => void;
    }) => {
      showModal(
        <>
          <EnableCashbackModal
            tip={modalContent?.tip || ''}
            subtitle={modalContent?.title || ''}
            onEnableCashback={onEnableCashback}
            onGoToStoreWithoutCashback={createHandlerWithCloseModal(
              onGoToStoreWithoutCashback
            )}
          />
          <LockScrollGlobally lock />
        </>,
        {
          style: modalStyle,
        }
      );
    },
    [createHandlerWithCloseModal, showModal, modalContent]
  );

  return { showEnableCashbackModal };
};
